import { createSignal, type Accessor } from 'solid-js'

export type LegendState = Record<string, boolean>

export interface UseLegendStateReturn<T extends LegendState> {
  state: Accessor<T>
  toggle: (key: string) => void
  isActive: (key: string) => boolean
  activeCount: () => number
  reset: () => void
}

export const useLegendState = <T extends LegendState>(initialState: T): UseLegendStateReturn<T> => {
  const [state, setState] = createSignal<T>({ ...initialState } as T)

  const toggle = (key: string) => {
    setState((prev) => {
      const activeCount = Object.values(prev).filter(Boolean).length

      if (prev[key] && activeCount <= 1) {
        return prev
      }

      return { ...prev, [key]: !prev[key] } as T
    })
  }

  const isActive = (key: string): boolean => {
    return state()[key] ?? false
  }

  const activeCount = (): number => {
    return Object.values(state()).filter(Boolean).length
  }

  const reset = () => {
    setState(() => ({ ...initialState }) as T)
  }

  return {
    state,
    toggle,
    isActive,
    activeCount,
    reset
  }
}

export interface SeriesConfig {
  key: string
  name: string
  color: string
  initialActive?: boolean
}

export const createLegendStateFromSeries = (seriesConfig: SeriesConfig[]): LegendState => {
  return seriesConfig.reduce(
    (acc, series) => ({
      ...acc,
      [series.key]: series.initialActive ?? true
    }),
    {} as LegendState
  )
}
